import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { HOME_TITLE, DEFAULT_DESCRIPTION, siteStructuredDataForPage, useSEO } from '@/lib/seo';
import type { Product } from '@shopgnd/shared';
import { listProducts } from '@/api/catalog';
import { subscribeCatalogRefresh } from '@/lib/catalog-refresh';
import { ProductCard } from '@/components/ProductCard';
import { HeroCarousel } from '@/components/HeroCarousel';
import { useHomepageHero } from '@/hooks/useHomepageHero';
import { FeaturedBusinessesStrip } from '@/components/home/FeaturedBusinessesStrip';
import { PlatformStatsBar } from '@/components/home/PlatformStatsBar';
import { ShopByParishSection } from '@/components/home/ShopByParishSection';
import { useCategories } from '@/hooks/useCategories';
import { useFeatureFlag, usePlatformConfig } from '@/hooks/usePlatformConfig';
import { shopCategoryHref } from '@/lib/categoryHelpers';
import { listHomeFeaturedCategories, listShopDepartmentCategories, MAX_HOME_FEATURED } from '@/lib/shopCategories';
import { buildUnsplashSrcSet, HERO_SIZES, isUnsplashUrl } from '@/lib/imageUrl';
import { HomepageFooterAd } from '@/components/home/HomepageFooterAd';
import type { Category } from '@shopgnd/shared';

const TILE_GRADIENTS = [
  'from-brand-700 to-brand-900',
  'from-slate-600 to-slate-900',
  'from-red-700 to-red-950',
] as const;

/** Shown until categories API responds (same layout as real tiles — not grey skeletons). */
const HOME_BROWSE_FALLBACK_TILES = [
  {
    id: 'fallback-shop',
    label: 'Shop',
    sub: 'Shop local vendors',
    to: '/grenada/shop',
    gradient: TILE_GRADIENTS[0],
    cta: 'Shop now →',
  },
  {
    id: 'fallback-electronics',
    label: 'Electronics',
    sub: 'Shop local vendors',
    to: '/grenada/shop/?category=electronics',
    gradient: TILE_GRADIENTS[1],
    cta: 'Shop now →',
    slug: 'electronics',
  },
  {
    id: 'fallback-fashion',
    label: 'Fashion',
    sub: 'Shop local vendors',
    to: '/grenada/shop/?category=fashion',
    gradient: TILE_GRADIENTS[2],
    cta: 'Shop now →',
  },
] as const;

const FIND_A_PRO_TILE = {
  id: 'find-a-pro',
  label: 'Find a Pro',
  sub: 'Electricians, plumbers & more',
  to: '/grenada/find-a-pro',
  gradient: 'from-amber-500 to-amber-700',
  cta: 'Find a pro →',
} as const;

type CategoryTile = {
  id: string;
  label: string;
  sub: string;
  to: string;
  gradient: string;
  cta: string;
  slug?: string;
};

function CategoryHomeTile({ tile }: { tile: CategoryTile }) {
  const isProTile = tile.id === FIND_A_PRO_TILE.id;

  return (
    <Link
      to={tile.to}
      className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${tile.gradient} p-5 text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg`}
    >
      <div className="relative z-10">
        <p className="text-xs font-semibold uppercase tracking-wide text-white/85">
          {isProTile ? 'Services' : 'Browse'}
        </p>
        <h2 className="mt-1 text-lg font-bold text-white">{tile.label}</h2>
        <p className="mt-0.5 text-xs text-white/90">{tile.sub}</p>
        <p className="mt-3 text-xs font-semibold text-accent-200 transition group-hover:text-white group-hover:underline">
          {tile.cta}
        </p>
      </div>
      <div
        className="pointer-events-none absolute right-[-20px] bottom-[-20px] h-[100px] w-[100px] rounded-full border border-white/10"
        aria-hidden
      />
    </Link>
  );
}

function categoryToTile(c: Category, index: number): CategoryTile {
  return {
    id: c.id,
    label: c.name,
    sub: 'Shop local vendors',
    to: shopCategoryHref(c),
    gradient: TILE_GRADIENTS[index % TILE_GRADIENTS.length],
    cta: 'Shop now →',
    slug: c.slug,
  };
}

export function HomePage() {
  const { slides: heroSlides, intervalMs } = useHomepageHero();
  const categories = useCategories();
  const { ready: platformConfigReady } = usePlatformConfig();
  const homePlatformStatsEnabled = useFeatureFlag('homePlatformStats');
  const showHomeStats = platformConfigReady && homePlatformStatsEnabled;
  const showShopByParish = useFeatureFlag('homeShopByParish');
  const [featured, setFeatured] = useState<Product[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);

  const categoryGrid = useMemo((): ReactNode[] => {
    const featured = listHomeFeaturedCategories(categories);
    const departments =
      featured.length > 0
        ? featured
        : listShopDepartmentCategories(categories).slice(0, MAX_HOME_FEATURED);

    const browseTiles: CategoryTile[] =
      departments.length > 0
        ? departments.map((c, i) => categoryToTile(c, i))
        : [...HOME_BROWSE_FALLBACK_TILES];

    const tiles = browseTiles.slice(0, MAX_HOME_FEATURED).map((tile) => (
      <CategoryHomeTile key={tile.id} tile={tile} />
    ));

    tiles.push(
      <CategoryHomeTile key={FIND_A_PRO_TILE.id} tile={{ ...FIND_A_PRO_TILE }} />,
    );
    return tiles;
  }, [categories]);

  const lcpHeroImage = useMemo(
    () => heroSlides.find((s) => s.image)?.image,
    [heroSlides],
  );

  useEffect(() => {
    if (!lcpHeroImage) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    if (isUnsplashUrl(lcpHeroImage)) {
      link.setAttribute('imagesrcset', buildUnsplashSrcSet(lcpHeroImage));
      link.setAttribute('imagesizes', HERO_SIZES);
    } else {
      link.href = lcpHeroImage;
    }
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [lcpHeroImage]);

  useSEO({
    title: HOME_TITLE,
    titleAsIs: true,
    description: DEFAULT_DESCRIPTION,
    path: '/',
    schema: siteStructuredDataForPage(),
  });

  useEffect(() => {
    const load = () => {
      setFeaturedLoading(true);
      void listProducts()
        .then(setFeatured)
        .finally(() => setFeaturedLoading(false));
    };
    load();
    return subscribeCatalogRefresh(load);
  }, []);

  return (
    <div>

      {/* ── Hero — HeroCarousel clears static hero after slides paint ── */}
      <HeroCarousel slides={heroSlides} intervalMs={intervalMs} />

      {showHomeStats ? <PlatformStatsBar /> : null}

      {/* ── Browse: categories + marketplace callouts ── */}
      <div className="mx-auto max-w-[1400px] space-y-8 px-4 py-8 sm:px-6">
        <section>
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                Marketplace
              </p>
              <h2 className="mt-1 text-2xl font-bold text-default">Shop by category</h2>
            </div>
            <Link
              to="/grenada/shop"
              className="shrink-0 text-sm font-semibold text-brand-700 hover:underline dark:text-brand-400"
            >
              All categories →
            </Link>
          </div>
          <div className="grid min-h-[140px] grid-cols-2 gap-3 lg:grid-cols-4">
            {categoryGrid}
          </div>
        </section>

        <section>
          <div className="grid gap-4 sm:grid-cols-3">
          <Link
            to="/grenada/vendors"
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-accent-500 to-amber-500 p-7 text-brand-900 transition hover:-translate-y-0.5 hover:shadow-xl"
          >
            <div className="pointer-events-none absolute right-[-20px] bottom-[-20px] h-[100px] w-[100px] rounded-full border border-brand-900/10" />
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-900/80">Marketplace</p>
            <h3 className="mt-2 text-xl font-bold">Local businesses</h3>
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-brand-800">
              Shop products, book salon appointments, or hire trades — all in one place.
            </p>
            <p className="mt-4 text-sm font-semibold text-brand-900 transition group-hover:underline">
              Explore businesses →
            </p>
          </Link>

          <Link
            to="/grenada/classifieds"
            className="group relative overflow-hidden rounded-2xl bg-[#005c34] p-7 text-white transition hover:-translate-y-0.5 hover:shadow-xl"
          >
            <div className="pointer-events-none absolute right-[-20px] bottom-[-20px] h-[100px] w-[100px] rounded-full border border-white/5" />
            <p className="text-xs font-semibold uppercase tracking-wide text-white/85">Community</p>
            <h3 className="mt-2 text-xl font-bold">Grenadian classifieds</h3>
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-white/90">
              Buy and sell cars, jobs, electronics, and more — or{' '}
              <span className="text-white">list property</span> for sale or rent.
            </p>
            <p className="mt-4 text-sm font-semibold text-accent-400 transition group-hover:underline">
              Browse listings →
            </p>
          </Link>

          <Link
            to="/grenada/vendors?mode=book"
            className="group relative overflow-hidden rounded-2xl bg-[#1a2a4a] p-7 text-white transition hover:-translate-y-0.5 hover:shadow-xl"
          >
            <div className="pointer-events-none absolute right-[-20px] bottom-[-20px] h-[100px] w-[100px] rounded-full border border-white/5" />
            <p className="text-xs font-semibold uppercase tracking-wide text-white/85">Book & hire</p>
            <h3 className="mt-2 text-xl font-bold">Salons & trades</h3>
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-slate-300">
              Book beauty and wellness appointments, or request quotes from electricians and plumbers.
            </p>
            <p className="mt-4 text-sm font-semibold text-accent-400 transition group-hover:underline">
              Book or hire →
            </p>
          </Link>
          </div>
        </section>
      </div>

      {/* ── Featured products ── */}
      <section className="border-y border-default bg-surface-raised py-10">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <div>
            <div className="mb-5 flex items-end justify-between gap-4">
              <h2 className="text-2xl font-bold text-default">Featured products</h2>
              <Link
                to="/grenada/shop"
                className="shrink-0 text-sm font-semibold text-brand-700 hover:underline dark:text-brand-400"
              >
                See all →
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {featuredLoading
              ? Array.from({ length: 4 }, (_, i) => (
                  <div
                    key={i}
                    className="aspect-[4/5] animate-pulse rounded-xl border border-default bg-surface-sunken"
                  />
                ))
              : featured.length > 0
                ? (
                  <>
                    {featured.slice(0, 3).map((p) => <ProductCard key={p.id} product={p} />)}
                    <Link
                      to="/boost"
                      className="group relative overflow-hidden rounded-xl border-2 border-brand-200 bg-brand-50 p-4 text-center transition hover:border-brand-400 hover:bg-brand-100 dark:border-brand-800 dark:bg-brand-900/30 dark:hover:border-brand-600"
                    >
                      <div className="absolute right-2 top-2">
                        <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-800 dark:text-brand-300">
                          Sponsored
                        </span>
                      </div>
                      <div className="flex h-full flex-col items-center justify-center gap-3 py-6">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-800">
                          <svg className="h-7 w-7 text-brand-600 dark:text-brand-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3h16.5M3.75 9h16.5M3.75 15h16.5M3.75 21h16.5" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-brand-800 dark:text-brand-200">Your product here</p>
                          <p className="mt-1 text-xs text-brand-600 dark:text-brand-400">Reach Grenadian shoppers daily</p>
                        </div>
                        <span className="text-xs font-bold text-brand-800 transition group-hover:underline dark:text-brand-200">
                          Get featured →
                        </span>
                      </div>
                    </Link>
                  </>
                )
                : (
                    <p className="col-span-full py-8 text-center text-sm text-muted">
                      No featured products yet.{' '}
                      <Link to="/grenada/shop" className="font-semibold text-brand-700 hover:underline dark:text-brand-400">
                        Browse the shop
                      </Link>
                    </p>
                  )}
            </div>
          </div>
        </div>
      </section>

      {showShopByParish ? <ShopByParishSection /> : null}

      <FeaturedBusinessesStrip />

      <HomepageFooterAd />

    </div>
  );
}
